// src/provider.tsx
import { useEffect, useMemo, useState } from "react";

// src/context.ts
import { createContext } from "react";
var FiasBridgeContext = createContext(null);

// src/theme.ts
var SYSTEM_FONTS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
var MONO_FONTS = '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace';
var SHARED_SPACING = { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" };
var SHARED_FONTS = { body: SYSTEM_FONTS, heading: SYSTEM_FONTS, mono: MONO_FONTS };
var SHARED_COMPONENTS = {
  borderRadius: "0.5rem",
  buttonRadius: "0.375rem",
  cardRadius: "0.5rem",
  inputRadius: "0.375rem",
  shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
  borderWidth: "1px"
};
var DARK_THEME = {
  mode: "dark",
  colors: {
    primary: "#ffffff",
    primaryText: "#0a0a0a",
    secondary: "#1f1f1f",
    accent: "#3b82f6",
    background: "#0a0a0a",
    surface: "#171717",
    card: "#141414",
    cardText: "#ffffff",
    text: "#ffffff",
    textSecondary: "#a6a6a6",
    muted: "#1e1e1e",
    mutedText: "#737373",
    border: "#2e2e2e",
    error: "#ef4444",
    warning: "#f59e0b",
    success: "#22c55e",
    info: "#3b82f6"
  },
  spacing: SHARED_SPACING,
  fonts: SHARED_FONTS,
  components: SHARED_COMPONENTS
};
var LIGHT_THEME = {
  mode: "light",
  colors: {
    primary: "#171717",
    primaryText: "#ffffff",
    secondary: "#e5e5e5",
    accent: "#2563eb",
    background: "#ffffff",
    surface: "#fafafa",
    card: "#ffffff",
    cardText: "#0a0a0a",
    text: "#0a0a0a",
    textSecondary: "#737373",
    muted: "#f5f5f5",
    mutedText: "#a3a3a3",
    border: "#e5e5e5",
    error: "#dc2626",
    warning: "#d97706",
    success: "#16a34a",
    info: "#2563eb"
  },
  spacing: SHARED_SPACING,
  fonts: SHARED_FONTS,
  components: SHARED_COMPONENTS
};
function getDefaultTheme(mode) {
  return mode === "light" ? LIGHT_THEME : DARK_THEME;
}

// src/bridge.ts
var messageCounter = 0;
function generateMessageId() {
  return `msg_${Date.now()}_${++messageCounter}`;
}
function mergeThemeWithDefaults(incoming) {
  const defaults = getDefaultTheme(incoming.mode ?? "dark");
  return {
    ...defaults,
    ...incoming,
    colors: { ...defaults.colors, ...incoming.colors ?? {} },
    spacing: { ...defaults.spacing, ...incoming.spacing ?? {} },
    fonts: { ...defaults.fonts, ...incoming.fonts ?? {} },
    components: { ...defaults.components, ...incoming.components ?? {} }
  };
}
var DEFAULT_TIMEOUT_MS = 3e4;
var FiasBridge = class {
  constructor() {
    this.pendingRequests = /* @__PURE__ */ new Map();
    this.initialized = false;
    this.initPromise = null;
    this.initResolve = null;
    this.archId = "";
    this.permissions = [];
    this.theme = null;
    this.currentPath = "";
    this.hostOrigin = "";
    this.themeListeners = /* @__PURE__ */ new Set();
    this.navigationListeners = /* @__PURE__ */ new Set();
    this.stepNavigationListeners = /* @__PURE__ */ new Set();
    this.listening = false;
    this.handleMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== "object" || !data.type) return;
      if (data.type === "init") {
        if (this.initialized) return;
        this.hostOrigin = event.origin;
        const initMsg = data;
        this.archId = initMsg.payload.archId;
        this.permissions = initMsg.payload.permissions;
        this.theme = mergeThemeWithDefaults(initMsg.payload.theme);
        this.currentPath = initMsg.payload.currentPath;
        this.initialized = true;
        this.initResolve?.();
        return;
      }
      if (this.hostOrigin && event.origin !== this.hostOrigin) return;
      if (data.type === "theme_update") {
        this.theme = mergeThemeWithDefaults(data.payload);
        for (const listener of this.themeListeners) {
          listener(this.theme);
        }
        return;
      }
      if (data.type === "navigate_update") {
        this.currentPath = data.payload;
        for (const listener of this.navigationListeners) {
          listener(this.currentPath);
        }
        return;
      }
      if (data.type === "step_navigate") {
        const { nodeId } = data.payload;
        if (typeof nodeId === "string") {
          for (const listener of this.stepNavigationListeners) {
            listener(nodeId);
          }
        }
        return;
      }
      if (data.type === "stream_token") {
        const { messageId, text } = data;
        const pending = this.pendingRequests.get(messageId);
        if (pending?.onToken && typeof text === "string") {
          pending.onToken(text);
        }
        return;
      }
      if (data.type === "response") {
        const response = data;
        const pending = this.pendingRequests.get(response.messageId);
        if (!pending) return;
        clearTimeout(pending.timer);
        this.pendingRequests.delete(response.messageId);
        if (response.error) {
          pending.reject(new Error(response.error));
        } else {
          pending.resolve(response.payload);
        }
      }
    };
    this.initPromise = new Promise((resolve) => {
      this.initResolve = resolve;
    });
    this.attach();
  }
  /**
   * Register the message listener. Safe to call multiple times —
   * will not add duplicate listeners.
   */
  attach() {
    if (typeof window !== "undefined" && !this.listening) {
      window.addEventListener("message", this.handleMessage);
      this.listening = true;
    }
  }
  /**
   * Wait for the host to send the init message.
   */
  async waitForInit() {
    if (this.initialized) return;
    return this.initPromise;
  }
  /**
   * Signal to the host that the plugin is loaded and ready.
   */
  ready() {
    this.send("ready", {});
  }
  /**
   * Request the host to resize the plugin iframe.
   */
  resize(height) {
    this.send("resize", { height });
  }
  /**
   * Show a platform toast notification.
   */
  showToast(message, variant = "info") {
    this.send("toast", { message, variant });
  }
  /**
   * Send a request to the host and wait for a response.
   */
  async request(type, payload, timeoutMs) {
    await this.waitForInit();
    return new Promise((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error(`Bridge request timed out: ${type}`));
      }, timeoutMs ?? DEFAULT_TIMEOUT_MS);
      this.pendingRequests.set(messageId, {
        resolve,
        reject,
        timer
      });
      this.send(type, payload, messageId);
    });
  }
  getArchId() {
    return this.archId;
  }
  getPermissions() {
    return [...this.permissions];
  }
  getTheme() {
    return this.theme;
  }
  getCurrentPath() {
    return this.currentPath;
  }
  onThemeUpdate(listener) {
    this.themeListeners.add(listener);
    return () => this.themeListeners.delete(listener);
  }
  onNavigationUpdate(listener) {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }
  onStepNavigationUpdate(listener) {
    this.stepNavigationListeners.add(listener);
    return () => this.stepNavigationListeners.delete(listener);
  }
  /**
   * Send a streaming entity_invoke request. Tokens arrive via stream_token
   * messages and are forwarded to the onToken callback. The returned promise
   * resolves with the final EntityInvocationResult when the host sends the
   * normal response message.
   */
  async requestStream(payload, onToken) {
    await this.waitForInit();
    return new Promise((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error("Bridge streaming request timed out: entity_invoke"));
      }, 12e4);
      this.pendingRequests.set(messageId, {
        resolve,
        reject,
        timer,
        onToken
      });
      this.send("entity_invoke", { ...payload, stream: true }, messageId);
    });
  }
  /**
   * Clean up event listeners and pending requests.
   * After destroy(), the bridge can be re-activated by calling attach().
   */
  destroy() {
    if (typeof window !== "undefined" && this.listening) {
      window.removeEventListener("message", this.handleMessage);
      this.listening = false;
    }
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error("Bridge destroyed"));
    }
    this.pendingRequests.clear();
    this.themeListeners.clear();
    this.navigationListeners.clear();
    this.stepNavigationListeners.clear();
  }
  send(type, payload, messageId) {
    if (typeof window === "undefined") return;
    const message = {
      type,
      messageId: messageId ?? generateMessageId(),
      payload
    };
    const targetOrigin = this.hostOrigin || "*";
    window.parent.postMessage(message, targetOrigin);
  }
};
var bridgeInstance = typeof window !== "undefined" ? new FiasBridge() : null;
function getBridge() {
  if (!bridgeInstance) {
    bridgeInstance = new FiasBridge();
  }
  return bridgeInstance;
}
function resetBridge() {
  bridgeInstance?.destroy();
  bridgeInstance = null;
}

// src/provider.tsx
import { jsx } from "react/jsx-runtime";
function FiasProvider({ children }) {
  const [ready, setReady] = useState(false);
  const bridge = useMemo(() => getBridge(), []);
  useEffect(() => {
    let mounted = true;
    bridge.waitForInit().then(() => {
      if (mounted) {
        bridge.ready();
        setReady(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, [bridge]);
  if (!ready) {
    return null;
  }
  return /* @__PURE__ */ jsx(FiasBridgeContext.Provider, { value: bridge, children });
}

// src/hooks.ts
import { useCallback, useContext, useEffect as useEffect2, useRef, useState as useState2 } from "react";
import { flushSync } from "react-dom";
function useBridge() {
  const bridge = useContext(FiasBridgeContext);
  if (!bridge) {
    throw new Error("useBridge must be used within a <FiasProvider>");
  }
  return bridge;
}
function useFiasUser() {
  const bridge = useBridge();
  const [user, setUser] = useState2(null);
  useEffect2(() => {
    let mounted = true;
    bridge.request("get_user", {}).then((data) => {
      if (mounted) setUser(data);
    });
    return () => {
      mounted = false;
    };
  }, [bridge]);
  return user;
}
function useFiasTheme() {
  const bridge = useBridge();
  const [theme, setTheme] = useState2(bridge.getTheme());
  useEffect2(() => {
    return bridge.onThemeUpdate(setTheme);
  }, [bridge]);
  return theme;
}
function useFiasStorage() {
  const bridge = useBridge();
  const readFile = useCallback(
    async (path) => {
      const res = await bridge.request(
        "storage_read",
        { path }
      );
      return res?.content ?? null;
    },
    [bridge]
  );
  const writeFile = useCallback(
    async (path, content) => {
      await bridge.request("storage_write", { path, content });
    },
    [bridge]
  );
  const listFiles = useCallback(
    async (prefix) => {
      const res = await bridge.request("storage_list", { prefix });
      return res?.files ?? [];
    },
    [bridge]
  );
  const deleteFile = useCallback(
    async (path) => {
      await bridge.request("storage_delete", { path });
    },
    [bridge]
  );
  return { readFile, writeFile, listFiles, deleteFile };
}
function useEntityInvocation() {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState2(false);
  const [result, setResult] = useState2(null);
  const [error, setError] = useState2(null);
  const [streamingText, setStreamingText] = useState2("");
  const invoke = useCallback(
    async (params) => {
      setIsLoading(true);
      setError(null);
      setStreamingText("");
      try {
        const res = await bridge.requestStream(params, (token) => {
          flushSync(() => {
            setStreamingText((prev) => prev + token);
          });
        });
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge]
  );
  return { invoke, isLoading, result, error, streamingText };
}
function useImageGeneration() {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState2(false);
  const [result, setResult] = useState2(null);
  const [error, setError] = useState2(null);
  const generate = useCallback(
    async (params) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await bridge.request("image_generate", params, 12e4);
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge]
  );
  return { generate, isLoading, result, error };
}
function useFiasNavigation() {
  const bridge = useBridge();
  const [currentPath, setCurrentPath] = useState2(bridge.getCurrentPath());
  useEffect2(() => {
    return bridge.onNavigationUpdate(setCurrentPath);
  }, [bridge]);
  const navigateTo = useCallback(
    (path) => {
      bridge.request("navigate", { path });
    },
    [bridge]
  );
  return { navigateTo, currentPath };
}
function usePersistentState(key, initialValue) {
  const bridge = useBridge();
  const [value, setValueInternal] = useState2(initialValue);
  const initializedRef = useRef(false);
  useEffect2(() => {
    bridge.request("storage_read", { path: `__state/${key}` }).then((result) => {
      if (result.exists && result.content !== null) {
        try {
          setValueInternal(JSON.parse(result.content));
        } catch {
        }
      }
      initializedRef.current = true;
    });
  }, [bridge, key]);
  const setValue = useCallback(
    (next) => {
      setValueInternal((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        bridge.request("storage_write", {
          path: `__state/${key}`,
          content: JSON.stringify(resolved)
        }).catch(() => {
        });
        return resolved;
      });
    },
    [bridge, key]
  );
  return [value, setValue];
}
function useStepNavigation(initialStep) {
  const bridge = useBridge();
  const [currentStep, setCurrentStep] = useState2(initialStep ?? null);
  useEffect2(() => {
    return bridge.onStepNavigationUpdate((nodeId) => {
      setCurrentStep(nodeId);
    });
  }, [bridge]);
  return { currentStep, setCurrentStep };
}

// src/fias.ts
var fias = {
  /**
   * Signal to the host that the plugin is loaded and ready.
   * Called automatically by FiasProvider — only use manually
   * if not using React.
   */
  ready() {
    getBridge().ready();
  },
  /**
   * Request the host to resize the plugin iframe to the given height.
   */
  resize(height) {
    getBridge().resize(height);
  },
  /**
   * Show a platform toast notification.
   */
  showToast(message, variant = "info") {
    getBridge().showToast(message, variant);
  }
};

// src/themes/catalog-data.ts
var DAISY_THEMES = [
  {
    id: "abyss",
    name: "Abyss",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#bdff00",
        primaryText: "#427600",
        secondary: "#cebef4",
        accent: "#505050",
        background: "#001e29",
        surface: "#00111d",
        card: "#00111d",
        cardText: "#ffd6a7",
        text: "#ffd6a7",
        textSecondary: "#564775",
        muted: "#003843",
        mutedText: "#ffd6a7",
        border: "#000611",
        error: "#f04e4f",
        warning: "#ffbf00",
        success: "#01df72",
        info: "#00bafe"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.5rem",
        buttonRadius: "2rem",
        cardRadius: "0.5rem",
        inputRadius: "0.25rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#bdff00", "#cebef4", "#001e29", "#505050"]
    }
  },
  {
    id: "acid",
    name: "Acid",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#ff00ff",
        primaryText: "#180017",
        secondary: "#ff6e00",
        accent: "#c8ff00",
        background: "#f8f8f8",
        surface: "#eeeeee",
        card: "#eeeeee",
        cardText: "#000000",
        text: "#000000",
        textSecondary: "#180400",
        muted: "#140151",
        mutedText: "#c7cadc",
        border: "#e1e1e1",
        error: "#ff0000",
        warning: "#ffe200",
        success: "#00ff8a",
        info: "#007fff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "1rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ff00ff", "#ff6e00", "#f8f8f8", "#c8ff00"]
    }
  },
  {
    id: "aqua",
    name: "Aqua",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#13ecf3",
        primaryText: "#015355",
        secondary: "#966fb3",
        accent: "#ffe999",
        background: "#1a368b",
        surface: "#162455",
        card: "#162455",
        cardText: "#b8e6fe",
        text: "#b8e6fe",
        textSecondary: "#f2f0fc",
        muted: "#05176c",
        mutedText: "#90baff",
        border: "#091444",
        error: "#ff7265",
        warning: "#d97708",
        success: "#18a34a",
        info: "#2563eb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#13ecf3", "#966fb3", "#1a368b", "#ffe999"]
    }
  },
  {
    id: "autumn",
    name: "Autumn",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#8c0327",
        primaryText: "#edd0d0",
        secondary: "#d85251",
        accent: "#d59b6b",
        background: "#f1f1f1",
        surface: "#dbdbdb",
        card: "#dbdbdb",
        cardText: "#141414",
        text: "#141414",
        textSecondary: "#110202",
        muted: "#826a5c",
        mutedText: "#e5e0dd",
        border: "#c5c5c5",
        error: "#d40014",
        warning: "#e97f16",
        success: "#499380",
        info: "#44adbb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#8c0327", "#d85251", "#f1f1f1", "#d59b6b"]
    }
  },
  {
    id: "black",
    name: "Black",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#3a3a3a",
        primaryText: "#ffffff",
        secondary: "#3a3a3a",
        accent: "#3a3a3a",
        background: "#000000",
        surface: "#141414",
        card: "#141414",
        cardText: "#d6d6d6",
        text: "#d6d6d6",
        textSecondary: "#ffffff",
        muted: "#3a3a3a",
        mutedText: "#ffffff",
        border: "#1b1b1b",
        error: "#ff0301",
        warning: "#ffff00",
        success: "#028002",
        info: "#0000ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0rem",
        buttonRadius: "0rem",
        cardRadius: "0rem",
        inputRadius: "0rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#3a3a3a", "#3a3a3a", "#000000", "#3a3a3a"]
    }
  },
  {
    id: "bumblebee",
    name: "Bumblebee",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#fdc700",
        primaryText: "#733e0a",
        secondary: "#ff8904",
        accent: "#000000",
        background: "#ffffff",
        surface: "#f5f5f5",
        card: "#f5f5f5",
        cardText: "#161616",
        text: "#161616",
        textSecondary: "#7c2808",
        muted: "#433f3a",
        mutedText: "#e6e4e3",
        border: "#e4e4e4",
        error: "#ff6266",
        warning: "#fcb700",
        success: "#00d390",
        info: "#00bafe"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#fdc700", "#ff8904", "#ffffff", "#000000"]
    }
  },
  {
    id: "business",
    name: "Business",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#1c4e80",
        primaryText: "#d0dae5",
        secondary: "#7c909a",
        accent: "#ea6947",
        background: "#202020",
        surface: "#1c1c1c",
        card: "#1c1c1c",
        cardText: "#cdcdcd",
        text: "#cdcdcd",
        textSecondary: "#050708",
        muted: "#23282e",
        mutedText: "#cecfd0",
        border: "#181818",
        error: "#ac3e31",
        warning: "#dbae5a",
        success: "#6bb187",
        info: "#0291d5"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.25rem",
        buttonRadius: "0rem",
        cardRadius: "0.25rem",
        inputRadius: "0.25rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#1c4e80", "#7c909a", "#202020", "#ea6947"]
    }
  },
  {
    id: "caramellatte",
    name: "Caramellatte",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#000000",
        primaryText: "#ffffff",
        secondary: "#370a00",
        accent: "#8c3f27",
        background: "#fff7ed",
        surface: "#feecd3",
        card: "#feecd3",
        cardText: "#7c2808",
        text: "#7c2808",
        textSecondary: "#ffd6a7",
        muted: "#c93400",
        mutedText: "#fff7ed",
        border: "#ffd6a7",
        error: "#ff6266",
        warning: "#fcb700",
        success: "#006044",
        info: "#193ab7"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "2rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "2px"
      }
    },
    preview: {
      colors: ["#000000", "#370a00", "#fff7ed", "#8c3f27"]
    }
  },
  {
    id: "cmyk",
    name: "Cmyk",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#45aeee",
        primaryText: "#020b13",
        secondary: "#e8488a",
        accent: "#fff234",
        background: "#ffffff",
        surface: "#eeeeee",
        card: "#eeeeee",
        cardText: "#161616",
        text: "#161616",
        textSecondary: "#130207",
        muted: "#1a1a1a",
        mutedText: "#cbcbcb",
        border: "#dedede",
        error: "#e93f33",
        warning: "#ee8134",
        success: "#823290",
        info: "#4ba8c0"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#45aeee", "#e8488a", "#ffffff", "#fff234"]
    }
  },
  {
    id: "coffee",
    name: "Coffee",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#db924c",
        primaryText: "#110802",
        secondary: "#273e3f",
        accent: "#11576d",
        background: "#261b25",
        surface: "#1e151d",
        card: "#1e151d",
        cardText: "#c59f61",
        text: "#c59f61",
        textSecondary: "#d0d5d5",
        muted: "#120c12",
        mutedText: "#c9c7c9",
        border: "#120a11",
        error: "#fc9581",
        warning: "#ffd260",
        success: "#9db787",
        info: "#8ecac1"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#db924c", "#273e3f", "#261b25", "#11576d"]
    }
  },
  {
    id: "corporate",
    name: "Corporate",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#0082ce",
        primaryText: "#ffffff",
        secondary: "#61738d",
        accent: "#009689",
        background: "#ffffff",
        surface: "#e8e8e8",
        card: "#e8e8e8",
        cardText: "#181a2a",
        text: "#181a2a",
        textSecondary: "#ffffff",
        muted: "#000000",
        mutedText: "#ffffff",
        border: "#d1d1d1",
        error: "#ff6266",
        warning: "#fdc700",
        success: "#00a43b",
        info: "#0090b5"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.25rem",
        buttonRadius: "0.25rem",
        cardRadius: "0.25rem",
        inputRadius: "0.25rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#0082ce", "#61738d", "#ffffff", "#009689"]
    }
  },
  {
    id: "cupcake",
    name: "Cupcake",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#44ebd3",
        primaryText: "#005d58",
        secondary: "#f9cbe5",
        accent: "#ffd6a7",
        background: "#faf7f5",
        surface: "#efeae6",
        card: "#efeae6",
        cardText: "#291334",
        text: "#291334",
        textSecondary: "#a0004a",
        muted: "#262629",
        mutedText: "#e4e4e7",
        border: "#e7e2df",
        error: "#fe1c55",
        warning: "#eeaf00",
        success: "#00ba7b",
        info: "#00a4f2"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "2rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "2px"
      }
    },
    preview: {
      colors: ["#44ebd3", "#f9cbe5", "#faf7f5", "#ffd6a7"]
    }
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#ff6596",
        primaryText: "#180408",
        secondary: "#00e8ff",
        accent: "#ce74ff",
        background: "#fff248",
        surface: "#f7e83a",
        card: "#f7e83a",
        cardText: "#000000",
        text: "#000000",
        textSecondary: "#001316",
        muted: "#111a3b",
        mutedText: "#fff248",
        border: "#e3d40e",
        error: "#ff5861",
        warning: "#ffbe00",
        success: "#00a96e",
        info: "#00b5ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0rem",
        buttonRadius: "0rem",
        cardRadius: "0rem",
        inputRadius: "0rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ff6596", "#00e8ff", "#fff248", "#ce74ff"]
    }
  },
  {
    id: "dim",
    name: "Dim",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#9fe88d",
        primaryText: "#091307",
        secondary: "#ff7d5d",
        accent: "#c792e9",
        background: "#2a303c",
        surface: "#242933",
        card: "#242933",
        cardText: "#b2ccd6",
        text: "#b2ccd6",
        textSecondary: "#160503",
        muted: "#1c212b",
        mutedText: "#b2ccd6",
        border: "#20252e",
        error: "#ffae9b",
        warning: "#efd057",
        success: "#62efbd",
        info: "#28ebff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#9fe88d", "#ff7d5d", "#2a303c", "#c792e9"]
    }
  },
  {
    id: "dracula",
    name: "Dracula",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#ff79c6",
        primaryText: "#16050e",
        secondary: "#bd93f9",
        accent: "#ffb86c",
        background: "#282a36",
        surface: "#232530",
        card: "#232530",
        cardText: "#f8f8f3",
        text: "#f8f8f3",
        textSecondary: "#0d0815",
        muted: "#414558",
        mutedText: "#d6d7db",
        border: "#1f202a",
        error: "#ff5555",
        warning: "#f1fa8c",
        success: "#51fa7b",
        info: "#8be9fd"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ff79c6", "#bd93f9", "#282a36", "#ffb86c"]
    }
  },
  {
    id: "emerald",
    name: "Emerald",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#66cc8a",
        primaryText: "#223d30",
        secondary: "#377cfb",
        accent: "#f68067",
        background: "#ffffff",
        surface: "#e8e8e8",
        card: "#e8e8e8",
        cardText: "#333c4d",
        text: "#333c4d",
        textSecondary: "#ffffff",
        muted: "#333c4d",
        mutedText: "#f9fafb",
        border: "#d1d1d1",
        error: "#ff5861",
        warning: "#ffbe00",
        success: "#00a96e",
        info: "#00b5ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#66cc8a", "#377cfb", "#ffffff", "#f68067"]
    }
  },
  {
    id: "fantasy",
    name: "Fantasy",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#6d0076",
        primaryText: "#e3cee4",
        secondary: "#0075c2",
        accent: "#ff8600",
        background: "#ffffff",
        surface: "#e8e8e8",
        card: "#e8e8e8",
        cardText: "#1f2937",
        text: "#1f2937",
        textSecondary: "#cfe4f4",
        muted: "#1f2937",
        mutedText: "#cdd0d3",
        border: "#d1d1d1",
        error: "#ff5861",
        warning: "#ffbe00",
        success: "#00a96e",
        info: "#00b5ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#6d0076", "#0075c2", "#ffffff", "#ff8600"]
    }
  },
  {
    id: "forest",
    name: "Forest",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#1fb854",
        primaryText: "#000000",
        secondary: "#1eb88e",
        accent: "#1fb8ab",
        background: "#1b1717",
        surface: "#161212",
        card: "#161212",
        cardText: "#cac9c9",
        text: "#cac9c9",
        textSecondary: "#000c07",
        muted: "#19362d",
        mutedText: "#cdd3d1",
        border: "#110d0d",
        error: "#ff5861",
        warning: "#ffbe00",
        success: "#00a96e",
        info: "#00b5ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "2rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#1fb854", "#1eb88e", "#1b1717", "#1fb8ab"]
    }
  },
  {
    id: "garden",
    name: "Garden",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#fe0075",
        primaryText: "#ffffff",
        secondary: "#8e4162",
        accent: "#5c7f67",
        background: "#e9e7e7",
        surface: "#d4d2d2",
        card: "#d4d2d2",
        cardText: "#100f0f",
        text: "#100f0f",
        textSecondary: "#ead7de",
        muted: "#291e00",
        mutedText: "#e9e7e7",
        border: "#bebdbd",
        error: "#ff5861",
        warning: "#ffbe00",
        success: "#00a96e",
        info: "#00b5ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#fe0075", "#8e4162", "#e9e7e7", "#5c7f67"]
    }
  },
  {
    id: "halloween",
    name: "Halloween",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#ff8f00",
        primaryText: "#131616",
        secondary: "#7a00c2",
        accent: "#42aa00",
        background: "#1b1816",
        surface: "#0b0908",
        card: "#0b0908",
        cardText: "#cdcdcd",
        text: "#cdcdcd",
        textSecondary: "#e3d4f6",
        muted: "#2f1b05",
        mutedText: "#d2ccc7",
        border: "#000000",
        error: "#f35248",
        warning: "#d97708",
        success: "#18a34a",
        info: "#2563eb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ff8f00", "#7a00c2", "#1b1816", "#42aa00"]
    }
  },
  {
    id: "lemonade",
    name: "Lemonade",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#419400",
        primaryText: "#010800",
        secondary: "#bdc000",
        accent: "#edd000",
        background: "#f8fdef",
        surface: "#e1e6d9",
        card: "#e1e6d9",
        cardText: "#151614",
        text: "#151614",
        textSecondary: "#0d0e00",
        muted: "#343300",
        mutedText: "#d2d3c7",
        border: "#cbcfc3",
        error: "#efc6c2",
        warning: "#d7d3b0",
        success: "#b9dbc6",
        info: "#b1d9e9"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#419400", "#bdc000", "#f8fdef", "#edd000"]
    }
  },
  {
    id: "lofi",
    name: "Lofi",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#0d0d0d",
        primaryText: "#ffffff",
        secondary: "#1a1919",
        accent: "#262626",
        background: "#ffffff",
        surface: "#f5f5f5",
        card: "#f5f5f5",
        cardText: "#000000",
        text: "#000000",
        textSecondary: "#ffffff",
        muted: "#000000",
        mutedText: "#ffffff",
        border: "#ebebeb",
        error: "#ff9181",
        warning: "#ffce69",
        success: "#69fec3",
        info: "#5fcfdd"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.5rem",
        buttonRadius: "2rem",
        cardRadius: "0.5rem",
        inputRadius: "0.25rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#0d0d0d", "#1a1919", "#ffffff", "#262626"]
    }
  },
  {
    id: "luxury",
    name: "Luxury",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#ffffff",
        primaryText: "#161616",
        secondary: "#152747",
        accent: "#513448",
        background: "#09090b",
        surface: "#171618",
        card: "#171618",
        cardText: "#dca54d",
        text: "#dca54d",
        textSecondary: "#cbd0d7",
        muted: "#331800",
        mutedText: "#ffe7a4",
        border: "#1e1d1f",
        error: "#ff6f6f",
        warning: "#e2d563",
        success: "#87d03a",
        info: "#67c6ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ffffff", "#152747", "#09090b", "#513448"]
    }
  },
  {
    id: "night",
    name: "Night",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#3abdf7",
        primaryText: "#010d15",
        secondary: "#818cf8",
        accent: "#f471b5",
        background: "#0f172a",
        surface: "#0c1425",
        card: "#0c1425",
        cardText: "#c9cbd0",
        text: "#c9cbd0",
        textSecondary: "#060715",
        muted: "#1e293b",
        mutedText: "#cdd0d4",
        border: "#0a1120",
        error: "#fb7085",
        warning: "#f4bf51",
        success: "#2fd4bf",
        info: "#0ca5e9"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#3abdf7", "#818cf8", "#0f172a", "#f471b5"]
    }
  },
  {
    id: "nord",
    name: "Nord",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#5e81ac",
        primaryText: "#03060b",
        secondary: "#81a1c1",
        accent: "#88c0d0",
        background: "#eceff4",
        surface: "#e5e9f0",
        card: "#e5e9f0",
        cardText: "#2e3440",
        text: "#2e3440",
        textSecondary: "#06090d",
        muted: "#4c566a",
        mutedText: "#d8dee9",
        border: "#d8dee9",
        error: "#bf616a",
        warning: "#ebcb8b",
        success: "#a3be8d",
        info: "#b48ead"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.5rem",
        buttonRadius: "1rem",
        cardRadius: "0.5rem",
        inputRadius: "0.25rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#5e81ac", "#81a1c1", "#eceff4", "#88c0d0"]
    }
  },
  {
    id: "pastel",
    name: "Pastel",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#e9d4ff",
        primaryText: "#8000d9",
        secondary: "#feccd2",
        accent: "#a3f2ce",
        background: "#ffffff",
        surface: "#f9fafb",
        card: "#f9fafb",
        cardText: "#161616",
        text: "#161616",
        textSecondary: "#c50035",
        muted: "#61738d",
        mutedText: "#dfe5ed",
        border: "#e5e6e7",
        error: "#ff9fa0",
        warning: "#ffb667",
        success: "#7af1a7",
        info: "#51e8fb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "2rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "2px"
      }
    },
    preview: {
      colors: ["#e9d4ff", "#feccd2", "#ffffff", "#a3f2ce"]
    }
  },
  {
    id: "retro",
    name: "Retro",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#ff9fa0",
        primaryText: "#801518",
        secondary: "#b7f6cd",
        accent: "#d08700",
        background: "#ece3ca",
        surface: "#e4d8b4",
        card: "#e4d8b4",
        cardText: "#793205",
        text: "#793205",
        textSecondary: "#00642e",
        muted: "#56524c",
        mutedText: "#d4d0ce",
        border: "#dbca9b",
        error: "#ff6266",
        warning: "#f34700",
        success: "#00776f",
        info: "#0082ce"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.5rem",
        buttonRadius: "0.25rem",
        cardRadius: "0.5rem",
        inputRadius: "0.25rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ff9fa0", "#b7f6cd", "#ece3ca", "#d08700"]
    }
  },
  {
    id: "silk",
    name: "Silk",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#1c1c29",
        primaryText: "#e1ff00",
        secondary: "#1c1c29",
        accent: "#1c1c29",
        background: "#f7f5f3",
        surface: "#f3ede9",
        card: "#f3ede9",
        cardText: "#4b4743",
        text: "#4b4743",
        textSecondary: "#ff7700",
        muted: "#161616",
        mutedText: "#c2bdb9",
        border: "#e2ddd9",
        error: "#ff7878",
        warning: "#efc375",
        success: "#afd89e",
        info: "#78c8ff"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "2rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderWidth: "2px"
      }
    },
    preview: {
      colors: ["#1c1c29", "#1c1c29", "#f7f5f3", "#1c1c29"]
    }
  },
  {
    id: "sunset",
    name: "Sunset",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#ff865b",
        primaryText: "#160603",
        secondary: "#fd6f9c",
        accent: "#b387fa",
        background: "#121c22",
        surface: "#0e171e",
        card: "#0e171e",
        cardText: "#9fb9d0",
        text: "#9fb9d0",
        textSecondary: "#160409",
        muted: "#1b262c",
        mutedText: "#94a0a9",
        border: "#091319",
        error: "#ffbbbd",
        warning: "#f1c892",
        success: "#addfad",
        info: "#89e0eb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#ff865b", "#fd6f9c", "#121c22", "#b387fa"]
    }
  },
  {
    id: "synthwave",
    name: "Synthwave",
    source: "daisy",
    mode: "dark",
    theme: {
      mode: "dark",
      colors: {
        primary: "#f861b4",
        primaryText: "#500323",
        secondary: "#71d1fe",
        accent: "#ff8904",
        background: "#09002f",
        surface: "#120b3d",
        card: "#120b3d",
        cardText: "#a1b1ff",
        text: "#a1b1ff",
        textSecondary: "#042e49",
        muted: "#422ad5",
        mutedText: "#c6d2ff",
        border: "#1c184b",
        error: "#ec8c78",
        warning: "#fede1c",
        success: "#00d3bb",
        info: "#00bafe"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#f861b4", "#71d1fe", "#09002f", "#ff8904"]
    }
  },
  {
    id: "valentine",
    name: "Valentine",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#f43098",
        primaryText: "#ffffff",
        secondary: "#ab44ff",
        accent: "#71d1fe",
        background: "#fcf2f8",
        surface: "#f9e4f0",
        card: "#f9e4f0",
        cardText: "#c5005a",
        text: "#c5005a",
        textSecondary: "#f8f3fd",
        muted: "#830c41",
        mutedText: "#f9cbe5",
        border: "#f9cbe5",
        error: "#f82834",
        warning: "#ff8904",
        success: "#5ce8b3",
        info: "#51e8fb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "2rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#f43098", "#ab44ff", "#fcf2f8", "#71d1fe"]
    }
  },
  {
    id: "winter",
    name: "Winter",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#0069ff",
        primaryText: "#cee4ff",
        secondary: "#463aa2",
        accent: "#c148ac",
        background: "#ffffff",
        surface: "#f2f7fe",
        card: "#f2f7fe",
        cardText: "#394e6a",
        text: "#394e6a",
        textSecondary: "#d5d7ee",
        muted: "#021431",
        mutedText: "#c5cbd2",
        border: "#e3e9f4",
        error: "#e58b8b",
        warning: "#efd7bc",
        success: "#81cfd1",
        info: "#94e7fb"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "1rem",
        buttonRadius: "1rem",
        cardRadius: "1rem",
        inputRadius: "0.5rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#0069ff", "#463aa2", "#ffffff", "#c148ac"]
    }
  },
  {
    id: "wireframe",
    name: "Wireframe",
    source: "daisy",
    mode: "light",
    theme: {
      mode: "light",
      colors: {
        primary: "#d4d4d4",
        primaryText: "#242424",
        secondary: "#d4d4d4",
        accent: "#d4d4d4",
        background: "#ffffff",
        surface: "#f5f5f5",
        card: "#f5f5f5",
        cardText: "#161616",
        text: "#161616",
        textSecondary: "#242424",
        muted: "#d4d4d4",
        mutedText: "#242424",
        border: "#ebebeb",
        error: "#9d0410",
        warning: "#963b00",
        success: "#006044",
        info: "#005889"
      },
      spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
      },
      components: {
        borderRadius: "0.25rem",
        buttonRadius: "0rem",
        cardRadius: "0.25rem",
        inputRadius: "0.25rem",
        shadowSm: "none",
        shadowMd: "none",
        shadowLg: "none",
        borderWidth: "1px"
      }
    },
    preview: {
      colors: ["#d4d4d4", "#d4d4d4", "#ffffff", "#d4d4d4"]
    }
  }
];

// src/themes/index.ts
var AI_AUTO_ENTRY = {
  id: "ai-auto",
  name: "AI's Choice",
  source: "ai",
  mode: "dark",
  theme: DARK_THEME,
  // placeholder; AI picks the real theme at runtime
  preview: {
    colors: ["#8b5cf6", "#ec4899", "#1e1e2e", "#06b6d4"]
  }
};
var NO_THEME_ENTRY = {
  id: "no-theme",
  name: "Custom from Scratch",
  source: "none",
  mode: "dark",
  theme: DARK_THEME,
  // placeholder; AI creates its own design
  preview: {
    colors: ["#f472b6", "#38bdf8", "#1a1a2e", "#a78bfa"]
  }
};
var FIAS_ENTRIES = [
  {
    id: "fias-dark",
    name: "Fias Dark",
    source: "fias",
    mode: "dark",
    theme: DARK_THEME,
    preview: {
      colors: [
        DARK_THEME.colors.primary,
        DARK_THEME.colors.secondary,
        DARK_THEME.colors.background,
        DARK_THEME.colors.accent
      ]
    }
  },
  {
    id: "fias-light",
    name: "Fias Light",
    source: "fias",
    mode: "light",
    theme: LIGHT_THEME,
    preview: {
      colors: [
        LIGHT_THEME.colors.primary,
        LIGHT_THEME.colors.secondary,
        LIGHT_THEME.colors.background,
        LIGHT_THEME.colors.accent
      ]
    }
  }
];
var THEME_CATALOG = [
  ...FIAS_ENTRIES,
  AI_AUTO_ENTRY,
  NO_THEME_ENTRY,
  ...DAISY_THEMES
];
function getThemeById(id) {
  return THEME_CATALOG.find((t) => t.id === id);
}

// src/themes/fonts.ts
var FONT_PAIRINGS = [
  {
    id: "system",
    name: "System Default",
    heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    googleFontsUrl: ""
  },
  {
    id: "inter-jakarta",
    name: "Modern Sans",
    heading: "'Plus Jakarta Sans', sans-serif",
    body: "'Inter', sans-serif",
    mono: "'JetBrains Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
  },
  {
    id: "playfair-lato",
    name: "Editorial Serif",
    heading: "'Playfair Display', serif",
    body: "'Lato', sans-serif",
    mono: "'Source Code Pro', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Lato:wght@300;400;700&family=Source+Code+Pro:wght@400;500&display=swap"
  },
  {
    id: "space-grotesk",
    name: "Tech Forward",
    heading: "'Space Grotesk', sans-serif",
    body: "'Space Grotesk', sans-serif",
    mono: "'Space Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap"
  },
  {
    id: "poppins-nunito",
    name: "Friendly Rounded",
    heading: "'Poppins', sans-serif",
    body: "'Nunito', sans-serif",
    mono: "'Fira Code', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Nunito:wght@400;600;700&family=Fira+Code:wght@400;500&display=swap"
  },
  {
    id: "dm-sans-serif",
    name: "Clean Geometric",
    heading: "'DM Sans', sans-serif",
    body: "'DM Sans', sans-serif",
    mono: "'DM Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap"
  },
  {
    id: "outfit-work",
    name: "Professional",
    heading: "'Outfit', sans-serif",
    body: "'Work Sans', sans-serif",
    mono: "'IBM Plex Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Work+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap"
  },
  {
    id: "crimson-raleway",
    name: "Classic Elegant",
    heading: "'Crimson Pro', serif",
    body: "'Raleway', sans-serif",
    mono: "'Inconsolata', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@400;600;700&family=Raleway:wght@400;500;600&family=Inconsolata:wght@400;500&display=swap"
  },
  {
    id: "rubik-karla",
    name: "Soft Modern",
    heading: "'Rubik', sans-serif",
    body: "'Karla', sans-serif",
    mono: "'Ubuntu Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&family=Karla:wght@400;500;600&family=Ubuntu+Mono:wght@400;700&display=swap"
  },
  {
    id: "montserrat-open",
    name: "Bold Impact",
    heading: "'Montserrat', sans-serif",
    body: "'Open Sans', sans-serif",
    mono: "'Roboto Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700;800&family=Open+Sans:wght@400;500;600&family=Roboto+Mono:wght@400;500&display=swap"
  },
  {
    id: "lexend-atkinson",
    name: "Accessible",
    heading: "'Lexend', sans-serif",
    body: "'Atkinson Hyperlegible', sans-serif",
    mono: "'JetBrains Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&family=Atkinson+Hyperlegible:wght@400;700&family=JetBrains+Mono:wght@400;500&display=swap"
  },
  {
    id: "sora-manrope",
    name: "Startup",
    heading: "'Sora', sans-serif",
    body: "'Manrope', sans-serif",
    mono: "'Fira Code', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&family=Fira+Code:wght@400;500&display=swap"
  },
  {
    id: "caveat-quicksand",
    name: "Handwritten",
    heading: "'Caveat', cursive",
    body: "'Quicksand', sans-serif",
    mono: "'Fira Code', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&family=Quicksand:wght@400;500;600;700&family=Fira+Code:wght@400;500&display=swap"
  },
  {
    id: "bebas-source",
    name: "Bold Display",
    heading: "'Bebas Neue', sans-serif",
    body: "'Source Sans 3', sans-serif",
    mono: "'Source Code Pro', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Source+Sans+3:wght@400;500;600&family=Source+Code+Pro:wght@400;500&display=swap"
  },
  {
    id: "righteous-nunito",
    name: "Retro Fun",
    heading: "'Righteous', sans-serif",
    body: "'Nunito Sans', sans-serif",
    mono: "'Ubuntu Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Righteous&family=Nunito+Sans:wght@400;500;600;700&family=Ubuntu+Mono:wght@400;700&display=swap"
  },
  {
    id: "dancing-merriweather",
    name: "Elegant Script",
    heading: "'Dancing Script', cursive",
    body: "'Merriweather', serif",
    mono: "'Inconsolata', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Inconsolata:wght@400;500&display=swap"
  },
  {
    id: "pacifico-lato",
    name: "Playful",
    heading: "'Pacifico', cursive",
    body: "'Lato', sans-serif",
    mono: "'Roboto Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Pacifico&family=Lato:wght@300;400;700&family=Roboto+Mono:wght@400;500&display=swap"
  },
  {
    id: "abril-assistant",
    name: "Magazine",
    heading: "'Abril Fatface', serif",
    body: "'Assistant', sans-serif",
    mono: "'JetBrains Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Assistant:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
  },
  {
    id: "comfortaa-cabin",
    name: "Soft Rounded",
    heading: "'Comfortaa', sans-serif",
    body: "'Cabin', sans-serif",
    mono: "'DM Mono', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Comfortaa:wght@400;500;600;700&family=Cabin:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap"
  },
  {
    id: "cinzel-cormorant",
    name: "Luxury",
    heading: "'Cinzel', serif",
    body: "'Cormorant Garamond', serif",
    mono: "'Inconsolata', monospace",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cormorant+Garamond:wght@400;500;600&family=Inconsolata:wght@400;500&display=swap"
  }
];
function getFontPairingById(id) {
  return FONT_PAIRINGS.find((f) => f.id === id) ?? FONT_PAIRINGS[0];
}
export {
  DARK_THEME,
  FONT_PAIRINGS,
  FiasBridge,
  FiasProvider,
  LIGHT_THEME,
  THEME_CATALOG,
  fias,
  getBridge,
  getDefaultTheme,
  getFontPairingById,
  getThemeById,
  resetBridge,
  useEntityInvocation,
  useFiasNavigation,
  useFiasStorage,
  useFiasTheme,
  useFiasUser,
  useImageGeneration,
  usePersistentState,
  useStepNavigation
};
